import React, { useContext, useEffect, useState } from "react";
import { aesTableSettings, TableColumn, TableSettings } from "../../tableSettings";
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer,
} from "../common/AbstractListView";
import constants from "../../constants";
import { Search } from "../../filtering";
import {
  DateRenderer,
  DateTimeIncludeSecRenderer,
  DateTimeRenderer,
  FloatRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextAlignRightRenderer,
  TextRenderer,
} from "../../uiControls/SmartTableRenderers";
import JobListViewContext, {
  IJobListViewContext,
  RightClickContext,
} from "../common/JobListViewContext";
import {
  ActionButtonItem,
  borderColors,
  splitMenuItemsIntoSections,
} from "../../ui/ActionButton";
import ToolboxMenuItem from "../../uiControls/ToolboxMenuItem";
import useFunctionKeys from "../../hooks/useFunctionKeys";
import { JobNames } from "../../framework.core/JobNames";
import * as XCol from "../../framework.core/XExportColumn";
import Menu, { IfmsMenuItem } from "../../ui/menu/Menu";
import ListViewShortcutMenu from "../common/ListViewShortcutMenu";
import InfoAreaDrawer from "../../uiControls/infoArea/InfoAreaDrawer";
import { SuntekSettings, SystemSettings } from "../../ifmsTypes";
import ApplicationContext from "../../ApplicationContext";
import useGlobalOkDialog from "../../hooks/useGlobalOkDialog";
import PrimaryButton from "../../ui/PrimaryButton";
import Select from "../../ui/Select";
import { AesListItem } from "../../fms.data/models/AesListItem";
import fastStorage from "../../fastStorage";
import {
  AesStatus,
  AesAction,
  MotItems,
  MotType,
  SeverityValueToLabel,
} from "../../fms.enum/aes";

function statusRenderer({ data, row, cell }: any) {
  const divStyle = {
    borderColor: "",
    borderStyle: "",
    borderWidth: "1px",
    backgroundColor: "white",
    color: "black",
    display: "inline-block",
    width: "50px",
  };
  let statusText = "";
  let tooltip = "";
  const statusVal = cell.value ?? "";
  switch (statusVal) {
    case AesStatus.Accepted:
      statusText = "Accepted";
      tooltip = "Accepted";
      divStyle.backgroundColor = "#92e292";
      break;
    case AesStatus.Rejected:
      statusText = "Rejected";
      tooltip = "Rejected";
      divStyle.backgroundColor = "red";
      divStyle.color = "white";
      break;
    case AesStatus.Transmitted:
      statusText = "XMTD";
      tooltip = "Transmitted";
      divStyle.backgroundColor = "yellow";
      break;
    case AesStatus.Deleted:
      statusText = "Deleted";
      tooltip = "Deleted";
      divStyle.backgroundColor = "blue";
      divStyle.color = "white";
      break;
    case AesStatus.Error:
      statusText = "Error";
      tooltip = "Error";
      divStyle.backgroundColor = "silver";
      break;
    case AesStatus.Canceled:
      statusText = "Canceled";
      tooltip = "Canceled";
      break;
    case AesStatus.Pending:
      statusText = "Pending";
      tooltip = "Pending";
      break;
    default:
      statusText = "";
      break;
  }

  return (
    <div
      style={divStyle}
      data-tooltip={tooltip}
      data-cell-actual-value={cell.value ?? ""}
    >
      {statusText}
    </div>
  );
}

function exportPortRenderer({ row, cell }: any) {
  const tooltipText = cell.row.original.exportationPortName;
  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-tooltip={tooltipText}
    >
      {cell.value}
    </div>
  );
}

function unladingPortRenderer({ row, cell }: any) {
  const tooltipText = cell.row.original.unladingPortName;
  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-tooltip={tooltipText}
    >
      {cell.value}
    </div>
  );
}

function filingOptionRenderer({ data, row, cell }: any) {
  let result;
  let tooltip = "";

  if (cell.value) {
    switch (cell.value) {
      case "2":
        result = "PRE";
        tooltip = "Predeparture";
        break;
      case "4":
        result = "POST";
        tooltip = "Postdeparture";
        break;
    }
  } else {
    result = "";
  }

  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-tooltip={tooltip}
      data-cell-actual-value={cell.value ?? ""}
    >
      {result}
    </div>
  );
}

function filingActionRenderer({ data, row, cell }: any) {
  let actionText;

  switch (cell.value ?? "") {
    case AesAction.Add:
      actionText = "Add";
      break;
    case AesAction.Replace:
      actionText = "Replace";
      break;
    case AesAction.Change:
      actionText = "Change";
      break;
    case AesAction.Cancel:
      actionText = "Cancel";
      break;
    default:
      actionText = "";
      break;
  }

  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-cell-actual-value={cell.value ?? ""}
    >
      {actionText}
    </div>
  );
}

function severityRenderer({ data, row, cell }: any) {
  let result;

  if (cell.value) {
    result = SeverityValueToLabel[cell.value];
  } else {
    result = "";
  }

  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-cell-actual-value={cell.value ?? ""}
    >
      {result}
    </div>
  );
}

function motRenderer({ row, cell }: any) {
  const tooltipText =
    MotItems.find((x) => x.value.toString() === cell.value?.toString())
      ?.label ?? "";
  return (
    <div
      id={`${cell.column.id}-${row.index}`}
      className="ifms-table-default"
      data-tooltip={tooltipText}
    >
      {cell.value}
    </div>
  );
}

function RightClickMenu({ dismiss }: { dismiss: VoidFunction }) {
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: "Search", shortcut: "F5", separator: false, newCol: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) => ({
    label: (
      <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />
    ),
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
  }));

  function onMenuItemSelected(item: IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch("F5");
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu="rightClickContext"
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: "nowrap",
      }}
      menuStyle={(i: number) => ({
        maxHeight: "300px",
        overflowY: "hidden",
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function AesListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as AesListItem;
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const separatorStyle = !appContext.useDarkMode
    ? "ifms-common-vertical-separator"
    : "ifms-common-vertical-separator-darkmode";

  useEffect(() => {
    const hasSentDate = !!selectedElement?.sentDate;
    fastStorage["aes-selected-has-sent-date"] = hasSentDate;
    document.dispatchEvent(
      new CustomEvent("aes-selection-changed", { detail: { hasSentDate } }),
    );
  }, [selectedElement?.sentDate]);

  async function printAes(reportType: string) {
    try {
      if (!selectedElement) return; // not selected
      const apiResult = await appContext.api.printAes(
        selectedElement.id,
        reportType,
      );
      if (apiResult?.success) {
        const newWindow = window.open();
        if (newWindow) {
          const iframe = document.createElement("iframe");
          iframe.src = "data:application/pdf;base64," + apiResult.result;
          iframe.style.cssText = "border:0;width:100%;height:100%";

          newWindow.document.title =
            reportType === "EEI"
              ? "Electronic Export Information Report"
              : "AES Response Report";
          newWindow.document.body.style.margin = "0";
          newWindow.document.body.appendChild(iframe);
          newWindow.document.close();
        }
      } else {
        await showGlobalOkDialog(
          <p>{apiResult.message}</p>,
          false,
          null,
          "warning",
        );
      }
    } catch (error) {
      console.error("Print failed:", error);
    }
  }

  return (
    <div style={{ display: "contents" }}>
      <div className="ifms-common-no-vertical-separator">
        {appContext.suntekSettings?.company?.billingsTabVersion === 2 ? (
          <div className={separatorStyle} />
        ) : (
          <></>
        )}
      </div>

      <div className={separatorStyle}>
        <PrimaryButton
          size="regular"
          disabled={!selectedElement?.sentDate}
          onClick={() => printAes("EEI")}
        >
          {" "}
          EEI Report{" "}
        </PrimaryButton>
        &nbsp;
        <PrimaryButton
          size="regular"
          disabled={!selectedElement?.sentDate}
          onClick={() => printAes("Response")}
        >
          {" "}
          AES Response{" "}
        </PrimaryButton>
        &nbsp;
      </div>
    </div>
  );
}

function AesListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function formatData(data: Array<AesListItem>) {
  return data.map((d) => ({
    ...d,
  }));
}

function AesListViewExtras() {
  useFunctionKeys({ onFunction: onFunctionKey });

  const jobListContext = useContext(JobListViewContext);

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(
    () => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey],
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }
    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (["F11"].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      //hideLogHistory={true}
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.AesFiling}
      jobId={(jobListContext.selectedElement as AesListItem | null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent("none")}
    />
  );
}

/**
 * Concrete implementation for Aes List screen
 *
 * @class AesListView
 * @extends {AbstractListView}
 */
class AesListView extends AbstractListView {
  private static CustomizedFilterFields = [
    "status",
    "filingOption",
    "severity",
    "filingAction",
    "mot",
  ];

  async fetchAndFormatData(
    api: any,
    search: Search,
    pageNum: number,
    limit: number,
  ): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.AesFiling,
      "AesListItem",
      AesListItem.calculateSearchParameters(
        search,
        AesListItem.MapSearchFields,
        AesListView.CustomizedFilterFields,
      ),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return { data: [], totalCount: 0 };
    }

    return {
      data: formatData(apiResult.entities),
      totalCount: apiResult.totalCount,
    };
  }

  async exportFormatDataWithExcel(
    api: any,
    search: Search,
    pageNum: number,
    limit: number,
    pageSize: number,
    pagePerBatch: number,
  ): Promise<Array<any>> {
    const apiResult = await api.getListItems(
      JobNames.AesFiling,
      "AesListItem",
      AesListItem.calculateSearchParameters(
        search,
        AesListItem.MapSearchFields,
        AesListView.CustomizedFilterFields,
      ),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: "status",
        renderer: ({ value, onChange, onEnter }: any) => {
          return (
            <Select
              name="ifms-filter-status"
              value={value ?? ""}
              onChange={(e: any) => onChange(e.target.value)}
              items={[
                { value: AesStatus.Accepted, label: "Accepted" },
                { value: AesStatus.Rejected, label: "Rejected" },
                { value: AesStatus.Transmitted, label: "Transmitted" },
                { value: AesStatus.Pending, label: "Pending" },
                { value: AesStatus.Canceled, label: "Canceled" },
                { value: AesStatus.Deleted, label: "Deleted" },
              ]}
              applySearchWithEnter={onEnter}
            />
          );
        },
        searchValueExtractor: (v: any) => v ?? "",
        displayValueExtractor: (v: any) => {
          const map: Record<string, string> = {
            [AesStatus.Accepted]: "Accepted",
            [AesStatus.Rejected]: "Rejected",
            [AesStatus.Transmitted]: "Transmitted",
            [AesStatus.Pending]: "Pending",
            [AesStatus.Canceled]: "Canceled",
            [AesStatus.Deleted]: "Deleted",
          };
          return map[v] ?? v ?? "";
        },
      },
      {
        accessor: "filingOption",
        renderer: ({ value, onChange, onEnter }: any) => {
          return (
            <Select
              name="ifms-filter-filingoption"
              value={value ?? ""}
              onChange={(e: any) => onChange(e.target.value)}
              items={[
                { value: "2", label: "Predeparture" },
                { value: "4", label: "Postdeparture" },
              ]}
              applySearchWithEnter={onEnter}
            />
          );
        },
        searchValueExtractor: (v: any) => v ?? "",
        displayValueExtractor: (v: any) => {
          const map: Record<string, string> = { "2": "Predeparture", "4": "Postdeparture" };
          return map[v] ?? v ?? "";
        },
      },
      {
        accessor: "severity",
        renderer: ({ value, onChange, onEnter }: any) => {
          return (
            <Select
              name="ifms-filter-severity"
              value={value ?? ""}
              onChange={(e: any) => onChange(e.target.value)}
              items={[
                { value: "", label: "" },
                { value: "F", label: "Fatally" },
                { value: "W", label: "Warning" },
                { value: "V", label: "Verification" },
                { value: "C", label: "Compliance" },
                { value: "I", label: "Information" },
              ]}
              applySearchWithEnter={onEnter}
            />
          );
        },
        searchValueExtractor: (v: any) => v ?? "",
        displayValueExtractor: (v: any) => SeverityValueToLabel[v] ?? v ?? "",
      },
      {
        accessor: "filingAction",
        renderer: ({ value, onChange, onEnter }: any) => {
          return (
            <Select
              name="ifms-filter-filingaction"
              value={value ?? ""}
              onChange={(e: any) => onChange(e.target.value)}
              items={[
                { value: AesAction.Add, label: "Add" },
                { value: AesAction.Replace, label: "Replace" },
                { value: AesAction.Cancel, label: "Cancel" },
              ]}
              applySearchWithEnter={onEnter}
            />
          );
        },
        searchValueExtractor: (v: any) => v ?? "",
        displayValueExtractor: (v: any) => {
          const map: Record<string, string> = {
            [AesAction.Add]: "Add",
            [AesAction.Replace]: "Replace",
            [AesAction.Cancel]: "Cancel",
          };
          return map[v] ?? v ?? "";
        },
      },
      {
        accessor: "mot",
        renderer: ({ value, onChange, onEnter }: any) => {
          return (
            <Select
              name="ifms-filter-mot"
              value={value ?? ""}
              onChange={(e: any) => onChange(e.target.value)}
              items={[
                {
                  value: MotType.VesselNonContainerized.toString(),
                  label:
                    MotType.VesselNonContainerized +
                    " - Vessel, Non-containerized",
                },
                {
                  value: MotType.VesselContainerzied.toString(),
                  label:
                    MotType.VesselContainerzied + " - Vessel, Containerized",
                },
                {
                  value: MotType.Barge.toString(),
                  label: MotType.Barge + " - Barge",
                },
                {
                  value: MotType.Rail.toString(),
                  label: MotType.Rail + " - Rail",
                },
                {
                  value: MotType.RailContainerized.toString(),
                  label:
                    MotType.RailContainerized + " - Rail, Containerized",
                },
                {
                  value: MotType.Truck.toString(),
                  label: MotType.Truck + " - Truck",
                },
                {
                  value: MotType.TruckContainerized.toString(),
                  label:
                    MotType.TruckContainerized + " - Truck, Containerized",
                },
                {
                  value: MotType.Auto.toString(),
                  label: MotType.Auto + " - Auto",
                },
                {
                  value: MotType.Pedstrian.toString(),
                  label: MotType.Pedstrian + " - Pedestrian",
                },
                {
                  value: MotType.RoadOther.toString(),
                  label: MotType.RoadOther + " - Road, Other",
                },
                {
                  value: MotType.Air.toString(),
                  label: MotType.Air + " - Air",
                },
                {
                  value: MotType.AirContainerized.toString(),
                  label:
                    MotType.AirContainerized + " - Air, Containerized",
                },
                {
                  value: MotType.Mail.toString(),
                  label: MotType.Mail + " - Mail",
                },
                {
                  value: MotType.PassengerHandCarried.toString(),
                  label:
                    MotType.PassengerHandCarried +
                    " - Passenger, Hand-carried",
                },
                {
                  value: MotType.FixedTransportInstallations.toString(),
                  label:
                    MotType.FixedTransportInstallations +
                    " - Fixed Transport Installations",
                },
              ]}
              applySearchWithEnter={onEnter}
            />
          );
        },
        searchValueExtractor: (v: any) => v ?? "",
        displayValueExtractor: (v: any) =>
          MotItems.find((x) => x.value.toString() === v?.toString())?.label ?? v ?? "",
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(
    opType: string,
    systemSettings: SystemSettings,
    suntekSettings: SuntekSettings,
  ): ListRowRenderer[] {
    return AesListItem.getExportCollection().map((x) => {
      const colRenderer: ListRowRenderer = {
        accessor: x.dataIndex,
        Cell: TextAlignLeftRenderer,
      };

      if (x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if (x.align === XCol.XAlignType.Right)
        colRenderer.Cell = TextAlignRightRenderer;

      if (x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool)
        colRenderer.Cell = NullableCheckboxIconRenderer;
      else if (x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if (x.type === XCol.XCellType.DateTime)
        colRenderer.Cell = DateTimeRenderer;
      else if (x.type === XCol.XCellType.DateTimeWithSec)
        colRenderer.Cell = DateTimeIncludeSecRenderer;
      else if (x.type === XCol.XCellType.Double) {
        if (x.dataIndex === "totalPkgs")
          colRenderer.Cell = (props: any) => (
            <FloatRenderer {...props} decimals={0} />
          );
        else
          colRenderer.Cell = (props: any) => (
            <FloatRenderer {...props} decimals={2} />
          );
      } else if (x.dataIndex === "status") {
        colRenderer.Cell = statusRenderer;
      } else if (x.dataIndex === "filingAction") {
        colRenderer.Cell = filingActionRenderer;
      } else if (x.dataIndex === "severity") {
        colRenderer.Cell = severityRenderer;
      } else if (x.dataIndex === "filingOption") {
        colRenderer.Cell = filingOptionRenderer;
      } else if (x.dataIndex === "exportationPort") {
        colRenderer.Cell = exportPortRenderer;
      } else if (x.dataIndex === "unladingPort") {
        colRenderer.Cell = unladingPortRenderer;
      } else if (x.dataIndex === "mot") {
        colRenderer.Cell = motRenderer;
      }

      return colRenderer;
    });
  }

  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({
      accessor: "status",
      changedValue: (value: any) => {
        return value["status"];
      },
    });
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: "id",
      orderByAscending: false,
    };
  }

  getTableSettings(
    opType: string,
    systemSettings: SystemSettings,
    suntekSettings: SuntekSettings,
  ): TableSettings {
    const tsettings = aesTableSettings;
    const cols = AesListItem.getExportCollection();

    tsettings.columns = cols.map<TableColumn>((x) => {
      return {
        ...x,
        accessor: x.dataIndex,
        Header: x.text,
        filterType: undefined,
        filterTypeV2: x.type,
      };
    });
    return tsettings;
  }

  renderCommands() {
    return <AesListCommands />;
  }

  renderBeforeConfigureButton() {
    return <AesListToolbar />;
  }

  renderExtras() {
    return <AesListViewExtras />;
  }

  getViewUrlFor(row: AesListItem) {
    return "";
  }

  getListViewTitle() {
    return "AES List";
  }

  renderRowExtras(
    rowId: number | null,
    context: IJobListViewContext,
  ): React.ReactNode | null {
    return null;
  }

  renderAdditionalCommands() {
    return null;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(
    context: RightClickContext,
    dismiss: VoidFunction,
  ): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default AesListView;
